import { FramerConfigForm } from '@/components/integrations/framer/FramerConfigForm'
import { HowItWorks } from '@/components/integrations/framer/HowItWorks'
import { PageHeader } from '@/components/integrations/framer/PageHeader'
import { orpc } from '@/lib/orpc/client'
import { queryClient } from '@/lib/query'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'sonner'

export const Route = createFileRoute(
  '/_dashboard/settings/integrations/framer',
)({
  component: RouteComponent,
  beforeLoad: async () => {
    await queryClient.ensureQueryData(
      orpc.integrations.getIntegrations.queryOptions(),
    )
  },
})

function RouteComponent() {
  const integrationsQuery = useQuery(
    orpc.integrations.getIntegrations.queryOptions({
      select: (data) => {
        const framerIntegration = data.find((integration) => {
          return integration.config.type === 'framer'
        })
        if (framerIntegration?.config.type === 'framer') {
          return {
            ...framerIntegration,
            config: framerIntegration.config,
          }
        }
        return null
      },
    }),
  )

  const generateKeyMutation = useMutation(
    orpc.integrations.framer.generateFramerApiKey.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: orpc.integrations.getIntegrations.queryKey(),
        })
        toast.success('API key generated successfully')
      },
      onError: () => {
        toast.error('Failed to generate API key')
      },
    }),
  )

  const toggleEnabledMutation = useMutation(
    orpc.integrations.toggleIntegrationEnabled.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: orpc.integrations.getIntegrations.queryKey(),
        })
        toast.success('Integration updated successfully')
      },
      onError: () => {
        toast.error('Failed to update integration')
      },
    }),
  )

  const handleGenerateKey = () => {
    generateKeyMutation.mutate({})
  }

  const handleToggleEnabled = (checked: boolean) => {
    if (!integrationsQuery.data?.id) return
    toggleEnabledMutation.mutate({
      integrationId: integrationsQuery.data.id,
      enabled: checked,
    })
  }

  const isFramerConnected = !!integrationsQuery.data
  const apiKey = integrationsQuery.data?.config.apiKey || null

  return (
    <div className="max-w-4xl">
      <PageHeader />

      {isFramerConnected && <div className="mb-6" />}

      <FramerConfigForm
        apiKey={apiKey}
        isConnected={isFramerConnected}
        integrationEnabled={integrationsQuery.data?.enabled ?? false}
        onGenerateKey={handleGenerateKey}
        onToggleEnabled={handleToggleEnabled}
        isGenerating={generateKeyMutation.isPending}
        isTogglingEnabled={toggleEnabledMutation.isPending}
      />

      <div className="mt-6">
        <HowItWorks />
      </div>
    </div>
  )
}
